import React, { useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { RevealWrapper } from '../hooks/useReveal';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { Server, Database, Shield, Activity, MapPin, Sparkles, Rocket } from 'lucide-react';

// SEO Hook for Technik Page
const useTechnikSEO = () => {
  useEffect(() => {
    document.title = 'Nextcloud Hosting Technik | Hochverfügbare Cluster & S3 Architektur | Wolkenhelden';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Unsere Nextcloud Hosting Infrastruktur: hochverfügbarer Cluster, S3-Objektspeicher, deutsche Rechenzentren in Frankfurt. 99,9% Uptime, DSGVO-konform.');
    }
  }, []);
};

// Static Tech Card Component to avoid nested .map() issues with Babel
const TechCard = ({ icon: Icon, emoji, title, subtitle, points, index }) => (
  <RevealWrapper delay={index * 100}>
    <Card className="glass-card h-full hover:shadow-lg hover:-translate-y-1 transition-all border-primary/10 hover:border-primary/30" data-testid={`tech-stack-${index}`}>
      <CardHeader>
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-blue-500/20">
          <Icon className="h-7 w-7 text-primary" />
        </div>
        <CardTitle className="font-display text-2xl">{emoji} {title}</CardTitle>
        <CardDescription className="text-base font-medium">{subtitle}</CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="space-y-3">
          <li className="flex items-start gap-2 text-sm text-muted-foreground">
            <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary" />
            {points[0]}
          </li>
          <li className="flex items-start gap-2 text-sm text-muted-foreground">
            <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary" />
            {points[1]}
          </li>
          <li className="flex items-start gap-2 text-sm text-muted-foreground">
            <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary" />
            {points[2]}
          </li>
          <li className="flex items-start gap-2 text-sm text-muted-foreground">
            <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary" />
            {points[3]}
          </li>
        </ul>
      </CardContent>
    </Card>
  </RevealWrapper>
);

const LocationCard = ({ name, status, statusColor, features, index }) => (
  <RevealWrapper delay={index * 100}>
    <Card className="glass-card hover:shadow-lg transition-all" data-testid={`location-${index}`}>
      <CardHeader>
        <div className="flex items-center gap-3 mb-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-blue-500/20">
            <MapPin className="h-5 w-5 text-primary" />
          </div>
          <CardTitle className="font-display text-xl">{name}</CardTitle>
        </div>
        <Badge className={statusColor}>{status}</Badge>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li>{features[0]}</li>
          <li>{features[1]}</li>
          <li>{features[2]}</li>
        </ul>
      </CardContent>
    </Card>
  </RevealWrapper>
);

export const TechnikPage = () => {
  // Apply SEO
  useTechnikSEO();

  return (
    <div className="py-16 md:py-24" data-testid="technik-page">
      {/* Hero Section */}
      <section className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <RevealWrapper>
          <div className="mb-16 text-center">
            <Badge variant="secondary" className="mb-4 bg-gradient-to-r from-primary/20 to-blue-500/20 border-primary/30">
              ⚡ Nextcloud Hosting Infrastruktur
            </Badge>
            <h1 className="font-display text-4xl font-semibold tracking-tight sm:text-5xl">
              Hochverfügbares Nextcloud Hosting
              <br />
              <span className="bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">
                auf HA-Cluster & S3 🦸
              </span>
            </h1>
            <p className="mt-4 text-lg text-muted-foreground md:text-xl max-w-3xl mx-auto">
              Wir betreiben dein Managed Nextcloud so, wie man kritische Systeme heute baut:
              <strong> hochverfügbar, skalierbar, selbstheilend</strong> – in deutschen Rechenzentren.
            </p>
          </div>
        </RevealWrapper>

        {/* Tech Stack Cards - Hardcoded to avoid nested .map() */}
        <div className="grid gap-8 md:grid-cols-3 mb-24">
          <TechCard
            icon={Server}
            emoji="🚀"
            title="Hochverfügbarkeit"
            subtitle="Helden-Orchestrierung statt Bastellösung"
            points={[
              'Rolling Updates ohne Downtime – wie ein echter Held',
              'Self-Healing: automatische Neustarts bei Problemen',
              'Wenn ein Knoten ausfällt, übernimmt ein anderer – blitzschnell ⚡',
              'Skaliert nach Bedarf – deine Superkraft wächst mit'
            ]}
            index={0}
          />
          <TechCard
            icon={Database}
            emoji="☁️"
            title="Helden-Speicher (S3)"
            subtitle="Skaliert sauber mit deinen Daten"
            points={[
              'Schnell & robust als primärer Helden-Storage',
              'Ideal für viele Dateien & große Mengen',
              'Redundante Datenhaltung – dreifach abgesichert',
              'Kein lokales Disk-Limit – unbegrenzte Power 💪'
            ]}
            index={1}
          />
          <TechCard
            icon={Shield}
            emoji="🛡️"
            title="Helden-Schutzschild"
            subtitle="Updates & Patches: wir kümmern uns"
            points={[
              'Automatische Security-Patches – du schläfst ruhig',
              'Monitoring & Alerting rund um die Uhr',
              'Optionale Antivirus-Schicht für extra Schutz',
              'Regelmäßige Sicherheits-Audits'
            ]}
            index={2}
          />
        </div>
      </section>

      {/* Architecture Section */}
      <section className="bg-gradient-to-b from-muted/30 to-transparent py-16 md:py-24">
        <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <RevealWrapper>
            <div className="mb-12 text-center">
              <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
                💪 Gebaut für Helden-Verfügbarkeit
              </h2>
              <p className="mt-4 text-lg text-muted-foreground max-w-3xl mx-auto">
                Deine Helden-Wolke läuft in einem hochverfügbaren Cluster-Setup mit S3-Objektspeicher.
                Das ist nicht nur schnell – das ist auch <strong>stabil, skalierbar und selbstheilend</strong>.
              </p>
            </div>
          </RevealWrapper>

          <RevealWrapper delay={100}>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              <div className="glass-card rounded-xl p-6 text-center hover:shadow-lg transition-all" data-testid="arch-item-0">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-blue-500/20">
                  <Server className="h-6 w-6 text-primary" />
                </div>
                <p className="text-sm font-medium">🚀 Cluster-Orchestrierung für Helden-HA</p>
              </div>
              <div className="glass-card rounded-xl p-6 text-center hover:shadow-lg transition-all" data-testid="arch-item-1">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-blue-500/20">
                  <Database className="h-6 w-6 text-primary" />
                </div>
                <p className="text-sm font-medium">☁️ Helden-Speicher (S3) als primärer Storage</p>
              </div>
              <div className="glass-card rounded-xl p-6 text-center hover:shadow-lg transition-all" data-testid="arch-item-2">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-blue-500/20">
                  <Activity className="h-6 w-6 text-primary" />
                </div>
                <p className="text-sm font-medium">⚡ Monitoring & automatisierte Helden-Recovery</p>
              </div>
              <div className="glass-card rounded-xl p-6 text-center hover:shadow-lg transition-all" data-testid="arch-item-3">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-blue-500/20">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <p className="text-sm font-medium">🛡️ Updates und Security-Patches: managed</p>
              </div>
            </div>
          </RevealWrapper>
        </div>
      </section>

      {/* Locations Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <RevealWrapper>
            <div className="mb-12 text-center">
              <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
                🇩🇪 Helden-Standorte in Deutschland
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                Deine Daten bleiben in Deutschland – DSGVO-konform und heldenhaft abgesichert.
              </p>
            </div>
          </RevealWrapper>

          <div className="grid gap-8 md:grid-cols-2 max-w-4xl mx-auto">
            <LocationCard
              name="Frankfurt am Main"
              status="⚡ Aktiv"
              statusColor="bg-green-500/10 text-green-600 border-green-500/30"
              features={['🏢 Rechenzentrum Tier III', '✅ ISO 27001 zertifiziert', '🔗 Redundante Anbindung']}
              index={0}
            />
            <LocationCard
              name="Hamburg"
              status="🔧 In Einrichtung"
              statusColor="bg-amber-500/10 text-amber-600 border-amber-500/30"
              features={['🌍 Zweiter Helden-Standort', '🔄 Geo-Redundanz', '⬆️ Erhöhte Verfügbarkeit']}
              index={1}
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="pb-16">
        <div className="container mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <RevealWrapper>
            <Card className="glass-card border-primary/20 text-center overflow-hidden relative">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-blue-500 to-primary" />
              <CardHeader className="pt-8">
                <div className="flex justify-center mb-4">
                  <div className="p-4 rounded-full bg-gradient-to-br from-primary to-blue-600 shadow-lg shadow-primary/30">
                    <Sparkles className="h-8 w-8 text-white" />
                  </div>
                </div>
                <CardTitle className="font-display text-3xl sm:text-4xl">
                  Überzeugt? Werde zum <span className="text-primary">Cloud-Helden</span>!
                </CardTitle>
                <CardDescription className="text-lg mt-4">
                  Gebaut auf solider Helden-Technik, managed von echten Profis. 🦸
                </CardDescription>
              </CardHeader>
              <CardContent className="pb-8">
                <Button asChild size="lg" className="bg-gradient-to-r from-primary to-blue-600 hover:from-primary/90 hover:to-blue-600/90 shadow-lg shadow-primary/25">
                  <Link to="/preise" className="flex items-center gap-2" data-testid="technik-cta-button">
                    <Rocket className="h-5 w-5" />
                    Helden-Wolke konfigurieren
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </RevealWrapper>
        </div>
      </section>
    </div>
  );
};
